import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import setWith from 'lodash/setWith'
import unset from 'lodash/unset'
import { validateValue } from './formValidation'

/** antd `name` is a string, a number, or a path array. Normalise to an array. */
export const toPath = (name) => {
  if (name == null) return []
  return Array.isArray(name) ? name : [name]
}

export const pathKey = (name) => toPath(name).join('.')

/**
 * Headless form store behind `Form.useForm()`. Holds values, errors and touched
 * state, notifies subscribed fields, and runs validation.
 *
 * Kept free of React so the whole state machine is unit-testable on its own.
 */
export const createFormStore = () => {
  let values = {}
  let initialValues = {}
  let errors = new Map()
  let touched = new Set()
  /** path key -> { rules, dependencies, transform } registered by a mounted Form.Item */
  const fields = new Map()
  const listeners = new Set()
  let callbacks = {}

  const notify = (changedPaths) => {
    listeners.forEach((listener) => listener(changedPaths))
  }

  const readValue = (name) => {
    const path = toPath(name)
    if (path.length === 0) return values
    return get(values, path)
  }

  /** `setWith` + Object customizer keeps numeric segments as object keys, not arrays. */
  const writeValue = (name, value) => {
    const path = toPath(name)
    if (path.length === 0) {
      values = value ?? {}
      return
    }
    values = cloneDeep(values)
    setWith(values, path, value, Object)
  }

  const store = {
    // --- values -------------------------------------------------------------
    getFieldValue: (name) => readValue(name),

    getFieldsValue: (nameList, filterFunc) => {
      if (nameList === true || nameList == null) return cloneDeep(values)
      if (Array.isArray(nameList)) {
        return nameList.reduce((acc, name) => {
          setWith(acc, toPath(name), readValue(name), Object)
          return acc
        }, {})
      }
      if (typeof nameList === 'object' && nameList.strict) return cloneDeep(values)
      if (typeof filterFunc === 'function') {
        return Object.fromEntries(Object.entries(cloneDeep(values)).filter(([, value]) => filterFunc(value)))
      }
      return cloneDeep(values)
    },

    setFieldValue: (name, value) => {
      const previous = readValue(name)
      if (isEqual(previous, value)) return
      writeValue(name, value)
      touched.add(pathKey(name))
      store.revalidateDependents(name)
      callbacks.onValuesChange?.(buildChangedValues(name, value), cloneDeep(values))
      notify([pathKey(name)])
    },

    setFieldsValue: (partial) => {
      if (!partial || typeof partial !== 'object') return
      const changed = collectPaths(partial)
      values = mergeDeep(cloneDeep(values), partial)
      changed.forEach((key) => errors.delete(key))
      notify(changed)
    },

    /** antd `setFields([{ name, value, errors, touched }])` */
    setFields: (fieldData = []) => {
      const changed = []
      fieldData.forEach((field) => {
        const key = pathKey(field.name)
        changed.push(key)
        if ('value' in field) writeValue(field.name, field.value)
        if (field.errors) {
          if (field.errors.length) errors.set(key, field.errors)
          else errors.delete(key)
        }
        if (field.touched) touched.add(key)
      })
      notify(changed)
    },

    resetFields: (nameList) => {
      if (!nameList) {
        values = cloneDeep(initialValues)
        errors = new Map()
        touched = new Set()
        notify(null)
        return
      }
      nameList.forEach((name) => {
        const key = pathKey(name)
        const initial = get(initialValues, toPath(name))
        if (initial === undefined) {
          values = cloneDeep(values)
          unset(values, toPath(name))
        } else {
          writeValue(name, cloneDeep(initial))
        }
        errors.delete(key)
        touched.delete(key)
      })
      notify(nameList.map(pathKey))
    },

    // --- errors and touched -------------------------------------------------
    getFieldError: (name) => errors.get(pathKey(name)) ?? [],

    getFieldsError: (nameList) => {
      const source = nameList ? nameList.map(pathKey) : [...fields.keys()]
      return source.map((key) => ({
        name: key.split('.'),
        errors: errors.get(key) ?? [],
      }))
    },

    isFieldTouched: (name) => touched.has(pathKey(name)),
    isFieldsTouched: (nameList) =>
      nameList ? nameList.some((name) => touched.has(pathKey(name))) : touched.size > 0,

    // --- validation ---------------------------------------------------------
    validateField: async (name) => {
      const key = pathKey(name)
      const field = fields.get(key)
      if (!field) return []
      const messages = await validateValue(readValue(name), field.rules, {
        label: field.label,
        getFieldValue: store.getFieldValue,
      })
      if (messages.length) errors.set(key, messages)
      else errors.delete(key)
      notify([key])
      return messages
    },

    /**
     * antd `validateFields()`: resolves with the values, or rejects with
     * `{ errorFields, values, outOfDate }`.
     */
    validateFields: async (nameList) => {
      const keys = nameList ? nameList.map(pathKey) : [...fields.keys()]
      const errorFields = []

      for (const key of keys) {
        const field = fields.get(key)
        if (!field) continue
        // eslint-disable-next-line no-await-in-loop -- keeps error order deterministic
        const messages = await store.validateField(field.name)
        if (messages.length) errorFields.push({ name: toPath(field.name), errors: messages })
      }

      notify(keys)

      if (errorFields.length > 0) {
        throw { errorFields, values: cloneDeep(values), outOfDate: false }
      }
      return cloneDeep(values)
    },

    /** Re-runs any field that declared `dependencies` on the changed field. */
    revalidateDependents: (name) => {
      const key = pathKey(name)
      fields.forEach((field, fieldKey) => {
        if (fieldKey === key) return
        const dependsOnIt = field.dependencies?.some((dependency) => pathKey(dependency) === key)
        if (!dependsOnIt) return
        if (touched.has(fieldKey) || errors.has(fieldKey)) store.validateField(field.name)
      })
    },

    submit: () => {
      store
        .validateFields()
        .then((resolved) => callbacks.onFinish?.(resolved))
        .catch((failure) => callbacks.onFinishFailed?.(failure))
    },

    // --- registration -------------------------------------------------------
    registerField: (name, config) => {
      const key = pathKey(name)
      fields.set(key, { name, ...config })
      if (config.initialValue !== undefined && readValue(name) === undefined) {
        writeValue(name, config.initialValue)
      }
      return () => {
        fields.delete(key)
        errors.delete(key)
        if (config.preserve === false) {
          values = cloneDeep(values)
          unset(values, toPath(name))
        }
      }
    },

    getRegisteredFields: () => [...fields.keys()],

    setInitialValues: (next, force = false) => {
      initialValues = cloneDeep(next ?? {})
      if (force || Object.keys(values).length === 0) {
        values = mergeDeep(cloneDeep(initialValues), values)
        notify(null)
      }
    },

    setCallbacks: (next) => {
      callbacks = { ...callbacks, ...next }
    },

    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    /** Test seam. */
    _internal: () => ({ values, errors, touched, fields }),
  }

  return store
}

/** antd hands `onValuesChange` only the branch that changed. */
const buildChangedValues = (name, value) => {
  const changed = {}
  setWith(changed, toPath(name), value, Object)
  return changed
}

const collectPaths = (object, prefix = []) =>
  Object.entries(object).flatMap(([key, value]) => {
    const path = [...prefix, key]
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = collectPaths(value, path)
      return nested.length ? [path.join('.'), ...nested] : [path.join('.')]
    }
    return [path.join('.')]
  })

/** Plain-object deep merge; arrays are replaced, matching antd's setFieldsValue. */
const mergeDeep = (target, source) => {
  if (!source || typeof source !== 'object') return target
  Object.entries(source).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      target[key] = mergeDeep(
        target[key] && typeof target[key] === 'object' ? target[key] : {},
        value
      )
      return
    }
    target[key] = value
  })
  return target
}

export { mergeDeep }
